"use client";

import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";

import { css, sva } from "@/../styled-system/css";

const drawer = sva({
  slots: [
    "overlay",
    "close",
    "content",
    "header",
    "footer",
    "title",
    "description",
    "item",
  ],
  base: {
    overlay: {
      position: "fixed",
      inset: "0",
      zIndex: "50",
      bgColor: "black/50",
      "&[data-state=open]": {
        animateIn: true,
        fadeIn: 0,
      },
      "&[data-state=closed]": {
        animateOut: true,
        fadeOut: 0,
      },
    },
    close: {
      ml: "auto",
      mr: "auto",
      mt: "4",
      minH: "8px",
      w: "100px",
      rounded: "full",
      bgColor: "gray.300",
    },
    content: {
      position: "fixed",
      left: "0",
      right: "0",
      bottom: "0",
      zIndex: "50",
      display: "flex",
      h: "auto",
      flexDir: "column",
      roundedTopLeft: "10px",
      roundedTopRight: "10px",
      bgColor: "white",
      maxHeight: "95dvh",
    },
    header: {
      display: "grid",
      gap: "1.5",
      p: "4",
      textAlign: "center",
      sm: { textAlign: "left" },
    },
    footer: {
      mt: "auto",
      display: "flex",
      flexDir: "column",
      gap: "2",
      p: "4",
    },
    title: {
      fontSize: "lg",
      lineHeight: "none",
      fontWeight: "semibold",
      letterSpacing: "tight",
    },
    description: { fontSize: "sm", lineHeight: "sm" },
    item: {
      position: "relative",
      display: "flex",
      cursor: "pointer",
      userSelect: "none",
      alignItems: "center",
      rounded: "sm",
      px: "2",
      py: "2",
      fontSize: "sm",
      ring: "none",
      transition: "colors",
      transitionDuration: "0.15s",
      _focus: { backgroundColor: "brandBlue/15" },
      _disabled: { pointerEvents: "none", opacity: "0.5" },
      _hover: {
        backgroundColor: "brandBlue/15",
      },
    },
  },
});

const { overlay, close, content, header, footer, title, description, item } =
  drawer.raw();

type DrawerProps = React.ComponentProps<typeof DrawerPrimitive.Root> & {
  shouldScaleBackground?: boolean;
};

const Drawer: React.FC<DrawerProps> = ({
  shouldScaleBackground = true,
  ...props
}) => (
  <DrawerPrimitive.Root
    repositionInputs={false}
    shouldScaleBackground={shouldScaleBackground}
    {...props}
  />
);
Drawer.displayName = "Drawer";

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay> & {
    css?: object;
  }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(overlay, cssProp);

  return <DrawerPrimitive.Overlay ref={ref} className={className} {...props} />;
});
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
    css?: object;
  }
>(({ children, css: cssProp = {}, ...props }, ref) => {
  const className = css(content, cssProp);

  return (
    <DrawerPortal>
      <DrawerOverlay />
      <DrawerPrimitive.Content ref={ref} className={className} {...props}>
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  );
});
DrawerContent.displayName = "DrawerContent";

const DrawerItem = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
    inset?: boolean;
    css?: object;
    onSelect?: () => void;
  }
>(({ inset, css: cssProp = {}, children, onSelect, ...props }, ref) => {
  const className = css(item, inset && { paddingLeft: "8" }, cssProp);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const currentElement = event.currentTarget;
    const focusableItems = Array.from(
      currentElement.parentElement?.querySelectorAll('[tabindex="0"]') || [],
    ) as HTMLElement[];

    const currentIndex = focusableItems.indexOf(currentElement);

    if (event.key === "ArrowDown") {
      event.preventDefault();
      const nextIndex = (currentIndex + 1) % focusableItems.length;
      focusableItems[nextIndex]?.focus();
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      const prevIndex =
        (currentIndex - 1 + focusableItems.length) % focusableItems.length;
      focusableItems[prevIndex]?.focus();
    }

    if ((event.key === "Enter" || event.key === " ") && onSelect) {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <div
      ref={ref}
      className={className}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      role="menuitem"
      {...props}
    >
      {children}
    </div>
  );
});
DrawerItem.displayName = "DrawerItem";

const DrawerHeader = ({
  css: cssProp = {},
  containHandle = true,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  css?: object;
  containHandle?: boolean;
}) => {
  const className = css(header, cssProp);

  return (
    <>
      {containHandle && <div className={css(close)} />}
      <div className={className} {...props} />
    </>
  );
};
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({
  css: cssProp = {},
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { css?: object }) => {
  const className = css(footer, cssProp);

  return <div className={className} {...props} />;
};
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title> & {
    css?: object;
  }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(title, cssProp);

  return <DrawerPrimitive.Title ref={ref} className={className} {...props} />;
});
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description> & {
    css?: object;
  }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(description, cssProp);

  return (
    <DrawerPrimitive.Description ref={ref} className={className} {...props} />
  );
});
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerItem,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
};
